import type { TovSourceResponse } from "contracts/api/client-tov/sources.responses";
import type { TovVersionResponse } from "contracts/api/client-tov/version.responses";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { PILLAR } from "contracts/enums/metrics";
import { For, type JSX, Show } from "solid-js";
import type { SummaryQuickLook } from "../../enums/scan";
import { SUMMARY_QUICK_LOOK } from "../../enums/scan";
import { Fact } from "../../ui/Fact";
import { createTovStore } from "../website/tovStore";
import { coverageWord } from "./contentFitnessDimensionModel";
import { DrawerShell, DrawerSection as Section } from "./DrawerShell";
import { contentFitnessCoverageDetail, signalsForPillar } from "./scanModel";
import {
  establishedLinkedEvidenceCount,
  fitnessEmptyCopy,
  populatedGuidanceRows,
  sourceCountLabel,
  sourceCoverage,
  sourceTrustStatus,
  traceabilityStatus,
  versionStatusLabel,
} from "./summaryModel";
export function SummaryQuickLookDrawer(props: {
  kind: SummaryQuickLook;
  websiteId: string;
  data: OverviewResponse;
  onClose: () => void;
}) {
  const tov = createTovStore(() =>
    props.kind === SUMMARY_QUICK_LOOK.BRAND_GUIDELINES
      ? props.data.site.clientId
      : undefined,
  );
  const activeProfile = tov.activeProfile;
  // Only an activated version is guidance a scan actually used.
  const version = () => (tov.hasActiveVersion() ? tov.version() : null);
  const sources = tov.sources;
  const detail = () =>
    quickLookDetail(props.kind, props.websiteId, props.data, {
      profileName: activeProfile()?.name,
      version: version() ?? undefined,
      sources: sources() ?? undefined,
    });
  return (
    <DrawerShell
      kicker={detail().kicker}
      title={detail().title}
      ariaLabel={`${detail().title} quick look`}
      onClose={props.onClose}
      footer={
        <a class="scan-btn scan-btn-primary" href={detail().href}>
          {detail().cta}
        </a>
      }
    >
      {detail().body}
    </DrawerShell>
  );
}

function quickLookDetail(
  kind: SummaryQuickLook,
  websiteId: string,
  data: OverviewResponse,
  brand: {
    profileName: string | undefined;
    version: TovVersionResponse | undefined;
    sources: TovSourceResponse[] | undefined;
  },
): {
  kicker: string;
  title: string;
  body: JSX.Element;
  href: string;
  cta: string;
} {
  if (kind === SUMMARY_QUICK_LOOK.BRAND_GUIDELINES) {
    const version = brand.version;
    const sourceRows = brand.sources;
    const guidance = populatedGuidanceRows(version);
    return {
      kicker: "Assessment context",
      title: "Active Brand Guidelines and traceability",
      href: `/w/${websiteId}/scan/brand-guidelines`,
      cta: "Open Brand Guidelines",
      body: (
        <>
          <Section title="Guidance used in this scan">
            <dl class="signal-drawer-facts">
              <Fact label="Profile" value={brand.profileName ?? "Checking"} />
              <Fact label="Version" value={version?.label ?? "Checking"} />
              <Fact label="Status" value={versionStatusLabel(version)} />
            </dl>
          </Section>
          <Section title="Coverage">
            <dl class="signal-drawer-facts">
              <Fact label="Sources" value={sourceCountLabel(sourceRows)} />
              <Fact
                label="Sources processed"
                value={sourceCoverage(sourceRows)}
              />
              <Fact
                label="Linked evidence"
                value={
                  version
                    ? `${establishedLinkedEvidenceCount(version, sourceRows)} established`
                    : "Checking"
                }
              />
            </dl>
          </Section>
          <Section title="Guidance available">
            <Show
              when={guidance.length > 0}
              fallback={
                <p>No populated guidance categories are available yet.</p>
              }
            >
              <ul class="scan-evidence-list">
                <For each={guidance}>
                  {(row) => (
                    <li>
                      {row.value} {row.label}
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </Section>
          <Section title="Traceability">
            <dl class="signal-drawer-facts">
              <Fact
                label="Guidance evidence"
                value={traceabilityStatus(version, sourceRows)}
              />
              <Fact
                label="Guidance to findings"
                value={
                  version?.rules.length
                    ? "No active rule linkage"
                    : "Not configured"
                }
              />
              <Fact
                label="Source trust"
                value={sourceTrustStatus(data, sourceRows, version)}
              />
            </dl>
          </Section>
        </>
      ),
    };
  }
  const dimensions = signalsForPillar(data, PILLAR.CONTENT_FITNESS);
  return {
    kicker: "Content Fitness quick look",
    title: "Content Fitness dimensions scored in this scan.",
    href: `/w/${websiteId}/scan/pages`,
    cta: "Explore affected pages",
    body: (
      <>
        <Section title="Scored dimensions">
          <Show
            when={dimensions.length > 0}
            fallback={<p>{fitnessEmptyCopy()}</p>}
          >
            <ul class="scan-evidence-list">
              <For each={dimensions}>
                {(dimension) => (
                  <li>
                    {dimension.label}: {dimension.score}/100
                    {dimension.coverageLabel
                      ? ` (${dimension.coverageLabel}, ${coverageWord(dimension)}; ${
                          dimension.coverage
                            ? contentFitnessCoverageDetail(dimension.coverage)
                            : "page coverage unavailable"
                        })`
                      : ""}
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </Section>
        <Section title="How to read this">
          <p>
            Each displayed dimension is a v1.2 Content Fitness score on a 0-100
            scale. Assessment coverage shows how many criteria were scored in
            this run. Pages assessed but unable to produce a score remain
            visible in coverage rather than becoming zero.
          </p>
        </Section>
      </>
    ),
  };
}
